import type {
  FeatureAnnouncement,
  FeatureAnnouncementSurface,
} from "@/types/featureAnnouncement";
import {
  USER_ACKNOWLEDGEMENT_TYPES,
  type UserAcknowledgementDescriptor,
} from "@/utils/acknowledgements/userAcknowledgements";

const PHONE_SCRIPT_EDITOR_PATTERNS = [
  /^\/inbound\/scripts\/[^/]+$/,
  /^\/inbound\/script\/[^/]+\/[^/]+$/,
  /^\/outbound\/scripts\/(?!(?:share|text|email|blaster)(?:\/|$))[^/]+$/,
  /^\/outbound\/script\/(?!(?:text|email|blaster)\/)[^/]+\/[^/]+$/,
];

const SURFACE_PATTERNS: Record<FeatureAnnouncementSurface, RegExp[]> = {
  scriptEditor: [
    ...PHONE_SCRIPT_EDITOR_PATTERNS,
    /^\/outbound\/scripts\/(?:text|email|blaster)\/[^/]+$/,
    /^\/outbound\/script\/(?:text|email|blaster)\/[^/]+\/[^/]+$/,
  ],
  phoneScriptEditor: PHONE_SCRIPT_EDITOR_PATTERNS,
};

const SESSION_DISMISSED_KEY = "gail_feature_announcements_dismissed";

export function getFeatureAnnouncementSurfaces(
  pathname: string | null | undefined
): FeatureAnnouncementSurface[] {
  if (!pathname) return [];
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

  return (Object.keys(SURFACE_PATTERNS) as FeatureAnnouncementSurface[]).filter((surface) =>
    SURFACE_PATTERNS[surface].some((pattern) => pattern.test(path))
  );
}

export function selectFeatureAnnouncement(
  announcements: readonly FeatureAnnouncement[],
  surfaces: readonly FeatureAnnouncementSurface[],
  activeFlags: readonly string[]
): FeatureAnnouncement | null {
  return (
    announcements.find(
      (announcement) =>
        surfaces.includes(announcement.surface) && activeFlags.includes(announcement.flagKey)
    ) ?? null
  );
}

export type FeatureAnnouncementNavigation =
  | { kind: "external"; href: string }
  | { kind: "push"; href: string }
  | { kind: "replace"; href: string };

/**
 * A `?query` href stays on the current page and replaces the history entry,
 * so Back after closing whatever it opened doesn't reopen it.
 */
export function resolveFeatureAnnouncementNavigation(
  href: string,
  pathname: string
): FeatureAnnouncementNavigation {
  if (/^https?:\/\//i.test(href)) return { kind: "external", href };
  if (href.startsWith("?")) return { kind: "replace", href: `${pathname}${href}` };
  return { kind: "push", href };
}

type AnnouncementIdentity = Pick<FeatureAnnouncement, "id" | "version" | "perOrg">;

/**
 * Acknowledgements are stored per user, so a `perOrg` announcement folds the
 * org into its key to be acknowledged separately in each org.
 */
function acknowledgementKey(announcement: AnnouncementIdentity, orgId?: string | null): string {
  return announcement.perOrg && orgId ? `${announcement.id}:${orgId}` : announcement.id;
}

export function buildFeatureAnnouncementAcknowledgement(
  announcement: AnnouncementIdentity,
  orgId?: string | null
): UserAcknowledgementDescriptor {
  return {
    scope: "announcements",
    type: USER_ACKNOWLEDGEMENT_TYPES.announcement,
    key: acknowledgementKey(announcement, orgId),
    version: announcement.version,
  };
}

function readSessionDismissed(): string[] {
  if (globalThis.window === undefined) return [];
  try {
    const parsed: unknown = JSON.parse(
      globalThis.sessionStorage.getItem(SESSION_DISMISSED_KEY) ?? "[]"
    );
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function sessionKey(announcement: AnnouncementIdentity, orgId?: string | null): string {
  return `${acknowledgementKey(announcement, orgId)}:${announcement.version}`;
}

export function isFeatureAnnouncementDismissedThisSession(
  announcement: AnnouncementIdentity,
  orgId?: string | null
): boolean {
  return readSessionDismissed().includes(sessionKey(announcement, orgId));
}

/** Keeps the card hidden for the tab even when the acknowledgement write fails. */
export function markFeatureAnnouncementDismissedThisSession(
  announcement: AnnouncementIdentity,
  orgId?: string | null
): void {
  if (globalThis.window === undefined) return;
  const key = sessionKey(announcement, orgId);
  const dismissed = readSessionDismissed();
  if (dismissed.includes(key)) return;
  try {
    globalThis.sessionStorage.setItem(
      SESSION_DISMISSED_KEY,
      JSON.stringify([...dismissed, key])
    );
  } catch {
    // In-memory state still hides the card for the current render.
  }
}
